// src/utils/usernameService.js
// One unique username per user.
//   users/{uid}            -> { username, authorName, bio, usernameChangedAt, ... }
//   usernames/{lowercase}  -> { uid, createdAt }   (the uniqueness lock)

import {
  getFirestore,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth, updateProfile } from "firebase/auth";

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 20;
export const BIO_MAX = 500;
export const COOLDOWN_DAYS = 30;
export const COOLDOWN_MS = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

const RESERVED = new Set([
  "admin", "administrator", "root", "support", "help", "official", "staff",
  "moderator", "mod", "system", "null", "undefined", "api", "app", "www",
  "settings", "profile", "wallet", "saved", "journey", "login", "signup",
  "trescend",
]);

export class UsernameError extends Error {
  constructor(code, message, extra = {}) {
    super(message);
    this.code = code;
    Object.assign(this, extra);
  }
}

export const normalize = (value) => (value || "").trim().toLowerCase();

/** Returns an error message, or null when the username is valid. */
export function validateUsername(raw) {
  const name = normalize(raw);
  if (name.length < USERNAME_MIN) return `At least ${USERNAME_MIN} characters`;
  if (name.length > USERNAME_MAX) return `Maximum ${USERNAME_MAX} characters`;
  if (!/^[a-z0-9_.]+$/.test(name)) return "Use only letters, numbers, _ and .";
  if (name.startsWith(".") || name.endsWith(".")) return "Can't start or end with a dot";
  if (name.includes("..")) return "No two dots in a row";
  if (RESERVED.has(name)) return "This name is reserved";
  return null;
}

/** "Rahul Kumar" -> "rahul_kumar" */
export function suggestFromName(name, email = "") {
  let base = (name || email.split("@")[0] || "user")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_.]/g, "")
    .replace(/\.{2,}/g, ".")
    .replace(/^\.+|\.+$/g, "");
  if (base.length < USERNAME_MIN) base = `${base}user`;
  return base.slice(0, USERNAME_MAX).replace(/\.+$/, "");
}

/** 1 read. A name you already own counts as available. */
export async function isUsernameAvailable(name, uid) {
  const snap = await getDoc(doc(getFirestore(), "usernames", normalize(name)));
  return !snap.exists() || snap.data().uid === uid;
}

/** Up to `count` free alternatives. Only runs when the typed name is taken (max 5 reads). */
export async function getSuggestions(base, uid, count = 3) {
  const b = normalize(base);
  const withSuffix = (suffix) => b.slice(0, USERNAME_MAX - suffix.length) + suffix;
  const rnd = () => String(Math.floor(10 + Math.random() * 90));

  const candidates = [
    withSuffix("1"),
    withSuffix("_"),
    b.includes("_") ? b.replace(/_/g, ".") : null,
    withSuffix(rnd()),
    withSuffix(`_${rnd()}`),
  ];
  const unique = [...new Set(candidates.filter(Boolean))].filter(
    (c) => !validateUsername(c)
  );

  const checked = await Promise.all(
    unique.map(async (c) => ((await isUsernameAvailable(c, uid)) ? c : null))
  );
  return checked.filter(Boolean).slice(0, count);
}

/**
 * Saves username + bio in ONE transaction.
 * - Claims usernames/{name} (the rules reject overwriting someone else's name).
 * - Releases the old name.
 * - Writes username + authorName so search keeps working.
 * - Then mirrors the name into Auth displayName.
 */
export async function saveUsernameAndProfile({ uid, username, bio }) {
  const db = getFirestore();
  const name = normalize(username);

  const invalid = validateUsername(name);
  if (invalid) throw new UsernameError("invalid", invalid);

  const userRef = doc(db, "users", uid);
  const newRef = doc(db, "usernames", name);

  await runTransaction(db, async (tx) => {
    // All reads first, then writes.
    const userSnap = await tx.get(userRef);
    const newSnap = await tx.get(newRef);

    const data = userSnap.exists() ? userSnap.data() : {};
    const oldName = data.username || null;
    const patch = { bio: bio ?? data.bio ?? "" };

    if (oldName !== name) {
      if (newSnap.exists() && newSnap.data().uid !== uid) {
        throw new UsernameError("taken", "That username was just taken");
      }

      // Cooldown applies only when changing an existing username.
      const changedAt = data.usernameChangedAt?.toMillis?.() ?? 0;
      if (oldName && changedAt && Date.now() - changedAt < COOLDOWN_MS) {
        const daysLeft = Math.ceil((changedAt + COOLDOWN_MS - Date.now()) / 86400000);
        throw new UsernameError(
          "cooldown",
          `You can change your username again in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`
        );
      }

      if (!newSnap.exists()) {
        tx.set(newRef, { uid, createdAt: serverTimestamp() });
      }
      if (oldName) {
        tx.delete(doc(db, "usernames", oldName));
        patch.usernameChangedAt = serverTimestamp();
      }
      patch.username = name;
      patch.authorName = name;
    }

    tx.set(userRef, patch, { merge: true });
  });

  // Keep Auth displayName in sync (not critical if it fails).
  const authUser = getAuth().currentUser;
  if (authUser && authUser.displayName !== name) {
    try {
      await updateProfile(authUser, { displayName: name });
    } catch (e) {
      console.warn("Auth displayName sync failed", e);
    }
  }

  return name;
}

// localStorage flag so the popup check costs 0 reads after the first time.
const flagKey = (uid) => `username_done_${uid}`;

export const hasUsernameFlag = (uid) => {
  try {
    return localStorage.getItem(flagKey(uid)) === "1";
  } catch {
    return false;
  }
};

export const setUsernameFlag = (uid) => {
  try {
    localStorage.setItem(flagKey(uid), "1");
  } catch {
    /* ignore */
  }
};